import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    FormControlLabel,
    Link,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { formatCurrencyInCents, presentPhone, presentUuid } from '../../helpers/formatters';
import { applyEmail, applyName } from '../../helpers/captureMode';
import { formatCalendarDate, isVisitManagedPartnerModality, itemKindLabel, localISODate, partnerModalityLabel, partnerStatusLabel, visitFrequencyLabel, visitStatusLabel } from './partnerLabels';

function PartnerDetailPage() {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [partner, setPartner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deliverQty, setDeliverQty] = useState({});
    const [orderQty, setOrderQty] = useState({});
    const [orderPaid, setOrderPaid] = useState(false);
    const [upcomingVisits, setUpcomingVisits] = useState([]);
    const isConsignment = isVisitManagedPartnerModality(partner?.modality);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/commerce-partners/${uuid}`);
            const data = response?.data?.data || null;
            setPartner(data);
            const nextDeliver = {};
            (data?.items || []).forEach((item) => {
                const stock = (data.stock || []).find((s) => s.product_uuid === item.product_uuid);
                nextDeliver[item.product_uuid] = stock?.quantity_on_hand ?? 0;
            });
            setDeliverQty(nextDeliver);
            const from = localISODate();
            const horizon = new Date();
            horizon.setDate(horizon.getDate() + 90);
            const calendar = await api.get('/commerce-partners/calendar', { params: { from, to: localISODate(horizon) } });
            const rows = (calendar?.data?.data || []).filter((visit) => visit.partner_uuid === uuid);
            setUpcomingVisits(rows.slice(0, 8));
        } catch {
            setPartner(null);
            setUpcomingVisits([]);
        } finally {
            setLoading(false);
        }
    }, [uuid]);

    useEffect(() => {
        if (!enabled) return undefined;
        load();
        return undefined;
    }, [enabled, load]);

    if (flagLoading || loading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;
    if (!partner) return <Typography>Parceria não encontrada.</Typography>;

    const handleDeliver = async () => {
        const lines = (partner.items || []).map((item) => ({
            product_uuid: item.product_uuid,
            quantity: Math.max(0, Number(deliverQty[item.product_uuid]) || 0),
        }));
        if (!lines.length) {
            Swal.fire('Atenção', 'Não há produtos para atualizar o estoque.', 'warning');
            return;
        }
        try {
            const response = await api.post(`/commerce-partners/${uuid}/stock/deliver`, { lines });
            if (!response?.data?.success) {
                Swal.fire('Atenção', response?.data?.errors?.lines || response?.data?.errors?.quantity || response?.data?.error || 'Falha.', 'warning');
                return;
            }
            await load();
            Swal.fire('OK', 'Estoque no ponto atualizado.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao atualizar estoque.', 'error');
        }
    };

    const handleOrder = async () => {
        const lines = Object.entries(orderQty)
            .filter(([, qty]) => Number(qty) > 0)
            .map(([product_uuid, quantity]) => ({ product_uuid, quantity: Number(quantity) }));
        if (!lines.length) {
            Swal.fire('Atenção', 'Informe quantidades do pedido.', 'warning');
            return;
        }
        try {
            const response = await api.post(`/commerce-partners/${uuid}/orders`, { lines, paid: orderPaid });
            if (!response?.data?.success) {
                Swal.fire('Atenção', Object.values(response?.data?.errors || {})[0] || response?.data?.error || 'Falha.', 'warning');
                return;
            }
            setOrderQty({});
            setOrderPaid(false);
            await load();
            Swal.fire('OK', 'Pedido atacado registrado como venda.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha no pedido.', 'error');
        }
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Excluir parceria?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Excluir',
        });
        if (!result.isConfirmed) return;
        await api.delete(`/commerce-partners/${uuid}`);
        navigate('/admin/partners/list');
    };

    const handleDeleteVisit = async (visit) => {
        const confirmation = await Swal.fire({
            title: 'Remover visita?',
            icon: 'warning',
            html: `
                <div style="text-align:left">
                    <p>Esta ação é <strong>irreversível</strong> e remove tudo o que estiver associado a esta visita:</p>
                    <ul style="margin:0 0 0 1.1rem;padding:0">
                        <li>a venda vinculada (se existir);</li>
                        <li>pagamentos/transações vinculados a essa venda — <strong>inclusive se já estiverem conciliados no extrato</strong>;</li>
                        <li>o impacto no estoque do parceiro (vendas e reposições desta visita);</li>
                        <li>o impacto no estoque e no total vendido dos produtos no seu depósito.</li>
                    </ul>
                    <p style="margin-top:12px"><strong>Confirme apenas se deseja desfazer completamente o registro desta visita.</strong></p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Sim, remover tudo',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            focusCancel: true,
        });
        if (!confirmation.isConfirmed) {
            return;
        }
        try {
            const response = await api.delete(`/commerce-partners/visits/${visit.uuid}`);
            if (!response?.data?.success) {
                Swal.fire('Atenção', response?.data?.error || 'Falha ao remover visita.', 'warning');
                return;
            }
            await load();
            Swal.fire('OK', 'Visita removida.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao remover visita.', 'error');
        }
    };

    const nextOpenVisit = upcomingVisits.find((visit) => visit.status !== 'done');

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                <Typography color="text.primary">{applyName(partner.name)}</Typography>
            </Breadcrumbs>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" sx={{ mb: 2 }} spacing={1}>
                <Box>
                    <Typography variant="h5">{applyName(partner.name)}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip label={partnerModalityLabel(partner.modality)} />
                        <Chip label={partnerStatusLabel(partner.status)} variant="outlined" />
                    </Stack>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {nextOpenVisit ? (
                        <Button
                            variant="contained"
                            component={RouterLink}
                            to={`/admin/partners/visits/${nextOpenVisit.uuid}`}
                        >
                            Registrar visita
                        </Button>
                    ) : null}
                    <Button component={RouterLink} to={`/admin/partners/${uuid}/edit`} variant="outlined">Editar</Button>
                    <Button color="error" onClick={handleDelete}>Excluir</Button>
                </Stack>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1">Contato e endereço</Typography>
                <Typography variant="body2">{applyName(partner.contact_name) || '—'} · {presentPhone(partner.contact_phone) || '—'} · {applyEmail(partner.contact_email) || '—'}</Typography>
                <Typography variant="body2">
                    {[partner.address, partner.address_number, partner.address_complement, partner.neighborhood, partner.city, partner.state].filter(Boolean).join(', ') || 'Sem endereço'}
                    {partner.zip_code ? ` · CEP ${partner.zip_code}` : ''}
                </Typography>
                {isVisitManagedPartnerModality(partner.modality) ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Frequência: {visitFrequencyLabel(partner.visit_frequency)} · Próxima visita: {formatCalendarDate(nextOpenVisit?.planned_on || partner.first_visit_on)}
                    </Typography>
                ) : null}
            </Paper>

            {isConsignment ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Visitas</Typography>
                    {upcomingVisits.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhuma visita agendada. A agenda é gerada pela frequência cadastrada na edição da parceria.
                        </Typography>
                    ) : (
                        upcomingVisits.map((visit) => (
                            <Stack
                                key={visit.uuid}
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ sm: 'center' }}
                                sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                            >
                                <Typography sx={{ flex: 1 }}>
                                    {formatCalendarDate(visit.planned_on)} · {visitStatusLabel(visit.status)}
                                    {visit.order_uuid ? ` · Venda #${presentUuid(visit.order_uuid)}` : ''}
                                </Typography>
                                {visit.status === 'done' ? (
                                    <>
                                        <Button size="small" component={RouterLink} to={`/admin/partners/visits/${visit.uuid}/receipt`}>
                                            Comprovante
                                        </Button>
                                        <Button size="small" component={RouterLink} to={`/admin/partners/visits/${visit.uuid}`}>
                                            Ver registro
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="small" variant="contained" component={RouterLink} to={`/admin/partners/visits/${visit.uuid}`}>
                                        Registrar visita
                                    </Button>
                                )}
                                <Button size="small" color="error" onClick={() => handleDeleteVisit(visit)}>
                                    Remover
                                </Button>
                            </Stack>
                        ))
                    )}
                </Paper>
            ) : null}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Itens do acordo</Typography>
                {(partner.items || []).length === 0 ? (
                    <Stack spacing={1.5} alignItems="flex-start">
                        <Typography variant="body2" color="text.secondary">
                            Nenhum produto neste acordo. A entrega de estoque e as reposições usam os produtos cadastrados aqui.
                        </Typography>
                        <Button component={RouterLink} to={`/admin/partners/${uuid}/edit`} variant="contained">
                            Adicionar produtos
                        </Button>
                    </Stack>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Produto</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    {isConsignment ? null : <TableCell>Atacado</TableCell>}
                                    <TableCell>Varejo</TableCell>
                                    {isConsignment ? <TableCell>Repasse</TableCell> : null}
                                    {isConsignment ? <TableCell>Qtd. expositor</TableCell> : null}
                                    <TableCell>Mín. pedido</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(partner.items || []).map((item) => (
                                    <TableRow key={item.uuid || item.product_uuid}>
                                        <TableCell>{item.product_name}</TableCell>
                                        <TableCell>{itemKindLabel(item.item_kind)}</TableCell>
                                        {isConsignment ? null : <TableCell>{formatCurrencyInCents(item.wholesale_cents)}</TableCell>}
                                        <TableCell>{formatCurrencyInCents(item.suggested_retail_cents)}</TableCell>
                                        {isConsignment ? <TableCell>{formatCurrencyInCents(item.consignment_repasse_cents)}</TableCell> : null}
                                        {isConsignment ? <TableCell>{item.display_qty || '—'}</TableCell> : null}
                                        <TableCell>{item.min_reorder_qty}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {isVisitManagedPartnerModality(partner.modality) ? (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Estoque no ponto</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Use esta tela <strong>apenas</strong> para carga inicial ou para corrigir o saldo após erro de contagem.
                        Venda e reposição do dia a dia devem ser registradas em <strong>Registrar visita</strong>.
                    </Typography>
                    {(partner.items || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Cadastre os produtos do acordo para gerenciar o estoque no ponto.
                        </Typography>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Produto</TableCell>
                                            <TableCell>Saldo atual</TableCell>
                                            <TableCell>Nova quantidade</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(partner.items || []).map((item) => {
                                            const stock = (partner.stock || []).find((s) => s.product_uuid === item.product_uuid);
                                            return (
                                                <TableRow key={item.product_uuid}>
                                                    <TableCell>{item.product_name}</TableCell>
                                                    <TableCell>{stock?.quantity_on_hand ?? 0}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            inputProps={{ min: 0 }}
                                                            value={deliverQty[item.product_uuid] ?? 0}
                                                            onChange={(e) => setDeliverQty((prev) => ({ ...prev, [item.product_uuid]: e.target.value }))}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Button sx={{ mt: 2 }} variant="outlined" onClick={handleDeliver}>Salvar estoque</Button>
                        </>
                    )}
                </Paper>
            ) : (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Pedido adicional (atacado)</Typography>
                    {(partner.items || []).length === 0 ? (
                        <Stack spacing={1.5} alignItems="flex-start">
                            <Typography variant="body2" color="text.secondary">
                                Cadastre os produtos do acordo para registrar pedidos de atacado.
                            </Typography>
                            <Button component={RouterLink} to={`/admin/partners/${uuid}/edit`} variant="contained">
                                Adicionar produtos
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Produto</TableCell>
                                            <TableCell>Atacado</TableCell>
                                            <TableCell>Mín.</TableCell>
                                            <TableCell>Qtd</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(partner.items || []).map((item) => (
                                            <TableRow key={item.product_uuid}>
                                                <TableCell>{item.product_name}</TableCell>
                                                <TableCell>{formatCurrencyInCents(item.wholesale_cents)}</TableCell>
                                                <TableCell>{item.min_reorder_qty}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={orderQty[item.product_uuid] || ''}
                                                        onChange={(e) => setOrderQty((prev) => ({ ...prev, [item.product_uuid]: e.target.value }))}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <FormControlLabel
                                sx={{ mt: 2, display: 'flex' }}
                                control={
                                    <Switch
                                        checked={orderPaid}
                                        onChange={(e) => setOrderPaid(e.target.checked)}
                                        color="success"
                                    />
                                }
                                label={orderPaid ? 'Pagamento já recebido' : 'Pagamento pendente'}
                            />
                            <Button sx={{ mt: 2 }} variant="contained" onClick={handleOrder}>Registrar pedido</Button>
                        </>
                    )}
                </Paper>
            )}
        </Box>
    );
}

export default PartnerDetailPage;
